"use client"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PAGE_SIZE_OPTIONS, type PageSize } from "@/features/invoices/filters"

export interface InvoicePaginationProps {
  page: number
  pageSize: PageSize
  count: number
  disabled?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (size: PageSize) => void
}

export function InvoicePagination({
  page,
  pageSize,
  count,
  disabled = false,
  onPageChange,
  onPageSizeChange,
}: InvoicePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(count / pageSize))
  const rangeStart = count === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(page * pageSize, count)

  return (
    <div className="flex items-center justify-between mt-4 text-body text-text-secondary">
      <div className="flex items-center gap-3">
        <span>
          Showing {rangeStart}-{rangeEnd} of {count}
        </span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value) as PageSize)}
        >
          <SelectTrigger className="w-[110px] h-8" aria-label="Page size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size} / page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1 hover:bg-muted rounded disabled:opacity-50"
          disabled={page <= 1 || disabled}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="px-3 py-1 hover:bg-muted rounded disabled:opacity-50"
          disabled={page >= totalPages || disabled}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  )
}
